/* ==========================================================================
   Bundle discount guard

   Owns the lifecycle of the Bundle Builder's discount code. Loaded on every
   page, because the code has to be cleaned up on the pages where the bundle
   builder itself is not present.

   Why a discount code at all: bundle items go into the cart as individual
   SKU line items so the 3PL can pick them, which rules out variant pricing.
   A code has to be deliberately applied, so it cannot fire on a cart the
   builder never touched, which an automatic discount on the same collections
   would. Note what it does *not* buy: once applied, a code is scoped by its
   own admin conditions exactly like an automatic one, and knows nothing about
   `_bundle_id`. Confining the saving to the bundle is the discount's
   collection + minimum-quantity setup doing the work, not this script.

   Everything here is gated on `bb_discount_code` already being in storage —
   a key only ever written after a successful bundle add. A shopper who never
   touched the bundle builder does no cart writes, makes no extra requests,
   and can never have a hand-entered code cleared by this script.
   ========================================================================== */

(function () {
  'use strict';

  var DISCOUNT_KEY = 'bb_discount_code';
  var CART_FETCH_TIMEOUT = 4000;

  /* ---------- Storage (throws in some private modes) ----------

     localStorage, not sessionStorage: the cart survives a closed tab, so the
     code that belongs to it has to as well. A stale key is harmless — every
     read is validated against the real cart before it is acted on, and
     reconcile() drops the key as soon as the cart holds no bundle items.
     -------------------------------------------------------------------- */

  function read() {
    try {
      return localStorage.getItem(DISCOUNT_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function write(code) {
    try {
      localStorage.setItem(DISCOUNT_KEY, code);
    } catch (e) { /* noop */ }
  }

  function clear() {
    try {
      localStorage.removeItem(DISCOUNT_KEY);
    } catch (e) { /* noop */ }
  }

  /* ---------- Cart ---------- */

  function fetchCart() {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () { controller.abort(); }, CART_FETCH_TIMEOUT)
      : null;

    return fetch('/cart.js', {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (timer) clearTimeout(timer);
        return cart;
      })
      .catch(function (e) {
        if (timer) clearTimeout(timer);
        throw e;
      });
  }

  function applyDiscount(code) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then(function (r) { return r.json(); });
  }

  function isBundleItem(item) {
    return !!(item.properties && item.properties['_bundle_id']);
  }

  /**
   * The cart qualifies as soon as it holds a bundle line.
   *
   * This used to demand a cart of *nothing but* bundle lines, on the reasoning
   * that a mixed cart would leak the saving onto whatever else was in it. It
   * does not: the codes are scoped in admin to the 3 ml tester collections
   * with a minimum quantity, so Shopify already confines the discount to the
   * bundle's own components. A 50 ml bottle sits in masculine-50-ml /
   * feminine-50-ml / unisex-50-ml-1 and is billed in full either way.
   *
   * Verified against the cart API with no theme JS in play — 10 testers plus
   * one 50 ml bottle keeps the 24.70 saving and charges 34.95 for the bottle.
   * The old rule threw that saving away the moment a customer cross-sold
   * themselves anything, which is what it was reported for.
   *
   * Kept as its own predicate rather than inlining hasBundleItems: this is the
   * "should the code be on" question, which may narrow again later (a loose
   * tester from the code's own collection is the one case that still leaks),
   * while hasBundleItems answers "is the bundle still here" for storage.
   */
  function cartQualifies(cart) {
    return hasBundleItems(cart);
  }

  function hasBundleItems(cart) {
    return !!(cart && cart.items && cart.items.some(isBundleItem));
  }

  function discountApplied(cart) {
    return !!(cart && cart.discount_codes && cart.discount_codes.length > 0);
  }

  /**
   * The cart itself is the most durable record of which code belongs to this
   * bundle: submitToCart stamps `_bundle_discount` onto every line. Storage
   * can be cleared or unavailable; the cart cannot.
   */
  function recoverCodeFromCart(cart) {
    if (!cart || !cart.items) return '';
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties;
      if (props && props['_bundle_discount']) return props['_bundle_discount'];
    }
    return '';
  }

  function renderCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderCart === 'function') {
      drawer.renderCart(null, null, false, null, false);
    }
  }

  /* ---------- Reconciliation ---------- */

  /**
   * Brings the cart's discount back in line with what it holds. Three states:
   *
   *   qualifying   -> the code should be on. Re-apply it if it is not, which
   *                   is what restores the discount after the customer
   *                   removes the non-bundle item that suppressed it.
   *   mixed        -> the code comes off, but the bundle is still there, so
   *                   the code is *kept in storage*. Suppression, not
   *                   termination — otherwise it could never come back.
   *   no bundle    -> the bundle is gone for good. Drop the code and the key.
   */
  var reconciling = false;

  function reconcile() {
    if (reconciling || !read()) return;
    reconciling = true;

    fetchCart()
      .then(function (cart) {
        var code = recoverCodeFromCart(cart) || read();

        if (cartQualifies(cart)) {
          if (discountApplied(cart) || !code) return;
          return applyDiscount(code).then(function (updated) {
            write(code);
            if (discountApplied(updated)) renderCartDrawer();
          });
        }

        /* Only forget the code once no bundle line remains. */
        if (!hasBundleItems(cart)) clear();

        if (!discountApplied(cart)) return;
        return applyDiscount('').then(renderCartDrawer);
      })
      .catch(function () { /* noop — checkout interception is the backstop */ })
      .then(function () { reconciling = false; });
  }

  /* ---------- Checkout interception ----------

     Shopify does not carry a cart-level discount into checkout reliably, so
     a qualifying cart gets the code pushed onto the checkout URL instead.

     No stored code means no preventDefault, no fetch, and the link behaves
     exactly as the theme intends — so a failure in here can never strand a
     customer who was not using the bundle.
     ------------------------------------------------------------------- */

  function handleCheckout(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    var link = e.target.closest && e.target.closest('a[href*="/checkout"], a[href*="checkout.shopify"]');
    if (!link) return;

    var discount = read();
    if (!discount) return;

    e.preventDefault();
    e.stopPropagation();

    var base = link.href;
    var sep = base.indexOf('?') !== -1 ? '&' : '?';
    var settled = false;

    /* Whatever happens below, the customer reaches checkout. */
    function go(url) {
      if (settled) return;
      settled = true;
      window.location.href = url;
    }
    var failSafe = setTimeout(function () { go(base); }, CART_FETCH_TIMEOUT + 500);

    fetchCart()
      .then(function (cart) {
        clearTimeout(failSafe);

        /* The cart's own `_bundle_discount` outranks storage. */
        var code = recoverCodeFromCart(cart) || discount;

        if (cartQualifies(cart)) {
          go(base + sep + 'discount=' + encodeURIComponent(code));
          return;
        }

        if (!hasBundleItems(cart)) clear();

        applyDiscount('')
          .catch(function () { /* noop */ })
          .then(function () { go(base); });
      })
      .catch(function () {
        clearTimeout(failSafe);
        go(base);
      });
  }

  document.addEventListener('click', handleCheckout, true);

  /* ---------- Cart-change detection ----------

     The theme's cart drawer posts straight to /cart/change.js and re-renders
     itself — it dispatches no event we could listen for. Removing the item
     that suppressed the discount is therefore invisible to a load-time-only
     reconcile, which is exactly the case where the discount has to come back.

     So watch the drawer for re-renders instead. That couples to the drawer
     element and nothing else, and it catches any cart mutation that redraws
     it, including ones made by apps.

     reconcile() is idempotent — it returns early whenever the cart already
     matches its target state — so the render it triggers settles on the next
     pass rather than looping.
     -------------------------------------------------------------------- */

  var reconcileTimer = null;

  function scheduleReconcile() {
    if (!read()) return;
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 300);
  }

  function watchCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__bbWatched) return;
    drawer.__bbWatched = true;

    try {
      new MutationObserver(scheduleReconcile).observe(drawer, {
        childList: true,
        subtree: true
      });
    } catch (e) { /* noop */ }
  }

  function start() {
    watchCartDrawer();
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Returning from checkout via bfcache skips DOMContentLoaded entirely. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reconcile();
  });

  /* Consumed by bundle-builder.js after a successful add. */
  window.BundleDiscount = {
    set: write,
    clear: clear,
    read: read,
    apply: applyDiscount,
    fetchCart: fetchCart,
    isBundleItem: isBundleItem,
    cartQualifies: cartQualifies,
    recoverCodeFromCart: recoverCodeFromCart
  };
})();
